import { useMemo, type Ref } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Field, Input, Select } from '@leanin/ui';
import {
  CIRCLE_MAX_MEMBERS_CAP,
  CIRCLE_MAX_TOPICS,
  CIRCLE_TARGET_SIZE_MIN,
} from '@leanin/shared/circle-limits';
import {
  CIRCLE_MEETING_CADENCES,
  type CircleMeetingCadence,
} from '@leanin/shared/circle-meeting-cadence';
import { PillFilter } from '@/components/filters';
import { parseWholeNumberInput } from '@/lib/whole-number-input';
import { useTopics } from '@/lib/queries/resources';
import { DISCUSSION_TOPIC_DOMAINS } from './discussion-topics';

// What a leader tells us about how the Circle runs, on the create, onboarding
// and admin forms: how often it meets, the size it aims for, and up to
// CIRCLE_MAX_TOPICS discussion topics. All optional. Members never see these.
//
// Only active discussion topics can be picked. A stored
// topic switched off since stays stored on the server but is not shown, not
// counted toward the cap and not sent: the server replaces only a Circle's
// active topics.

const CADENCE_LABEL_KEY = {
  weekly: 'leaderFacts.cadenceWeekly',
  every_two_weeks: 'leaderFacts.cadenceEveryTwoWeeks',
  monthly: 'leaderFacts.cadenceMonthly',
} as const satisfies Record<CircleMeetingCadence, string>;

export type CircleLeaderFacts = {
  readonly meetingCadence: CircleMeetingCadence | null;
  /** The target-size input as typed; `parseCircleLeaderFacts` reads it. */
  readonly targetSize: string;
  /** May hold a stored topic switched off since; read through `activeTopicIds`. */
  readonly topicIds: readonly string[];
};

/** The active discussion topics, in registry order: the picker's options. */
export type DiscussionTopics = {
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly activeIds: ReadonlySet<string>;
};

export function useDiscussionTopics(options?: { readonly enabled?: boolean }): DiscussionTopics {
  const topics = useTopics(DISCUSSION_TOPIC_DOMAINS, options);
  return useMemo(() => {
    const list = topics.data ?? [];
    return {
      options: list.map((topic) => ({ value: topic.id, label: topic.label })),
      activeIds: new Set(list.map((topic) => topic.id)),
    };
  }, [topics.data]);
}

/** The picked topics a leader can see and edit: the active ones. */
export function activeTopicIds(topicIds: readonly string[], activeIds: ReadonlySet<string>): string[] {
  return topicIds.filter((id) => activeIds.has(id));
}

export const NO_CIRCLE_LEADER_FACTS: CircleLeaderFacts = { meetingCadence: null, targetSize: '', topicIds: [] };

export function circleLeaderFactsOf(circle: {
  readonly meetingCadence: CircleMeetingCadence | null;
  readonly targetSize: number | null;
  readonly topics: ReadonlyArray<{ readonly id: string }>;
}): CircleLeaderFacts {
  return {
    meetingCadence: circle.meetingCadence,
    targetSize: circle.targetSize === null ? '' : String(circle.targetSize),
    topicIds: circle.topics.map((t) => t.id),
  };
}

/** Errors a refused save shows. Over the topic cap needs none: the field says so live. */
export type CircleLeaderFactsErrors = {
  readonly targetSize?: true;
};

/** The facts as the create and update requests take them. */
export type ParsedCircleLeaderFacts = {
  readonly meetingCadence: CircleMeetingCadence | null;
  readonly targetSize: number | null;
  /** Active topics only: the server replaces only a Circle's active topics. */
  readonly topicIds: string[];
};

export function parseCircleLeaderFacts(
  v: CircleLeaderFacts,
  activeIds: ReadonlySet<string>,
):
  | { readonly ok: true; readonly value: ParsedCircleLeaderFacts }
  | { readonly ok: false; readonly errors: CircleLeaderFactsErrors } {
  const targetSize = parseWholeNumberInput(v.targetSize, {
    min: CIRCLE_TARGET_SIZE_MIN,
    max: CIRCLE_MAX_MEMBERS_CAP,
  });
  const topicIds = activeTopicIds(v.topicIds, activeIds);
  if (!targetSize.ok || topicIds.length > CIRCLE_MAX_TOPICS) {
    return { ok: false, errors: targetSize.ok ? {} : { targetSize: true } };
  }
  return { ok: true, value: { meetingCadence: v.meetingCadence, targetSize: targetSize.value, topicIds } };
}

type Props = {
  readonly value: CircleLeaderFacts;
  readonly onChange: (next: CircleLeaderFacts) => void;
  readonly errors: CircleLeaderFactsErrors;
  /** From `useDiscussionTopics`, which the form also validates against. */
  readonly topics: DiscussionTopics;
  readonly disabled?: boolean;
  /** The meeting cadence select, for a caller that lands on it. */
  readonly cadenceRef?: Ref<HTMLSelectElement> | undefined;
};

export function CircleLeaderFactsFields({ value, onChange, errors, topics, disabled = false, cadenceRef }: Props) {
  const tc = useTranslations('Circles');
  // Over the cap shows at once, not on save: a Circle can hold more than five
  // active topics after an admin turns a stored one back on.
  const selectedTopicIds = activeTopicIds(value.topicIds, topics.activeIds);

  return (
    <>
      <Field label={tc('leaderFacts.cadenceLabel')}>
        <Select
          ref={cadenceRef}
          value={value.meetingCadence ?? ''}
          options={[
            { value: '', label: tc('leaderFacts.cadenceNotSpecified') },
            ...CIRCLE_MEETING_CADENCES.map((c) => ({ value: c, label: tc(CADENCE_LABEL_KEY[c]) })),
          ]}
          onChange={(e) => {
            const next = CIRCLE_MEETING_CADENCES.find((c) => c === e.target.value) ?? null;
            onChange({ ...value, meetingCadence: next });
          }}
          disabled={disabled}
        />
      </Field>
      <Field
        label={tc('leaderFacts.targetSizeLabel')}
        description={tc('leaderFacts.targetSizeDescription')}
        error={
          errors.targetSize === true
            ? tc('leaderFacts.errorTargetSize', { min: CIRCLE_TARGET_SIZE_MIN, max: CIRCLE_MAX_MEMBERS_CAP })
            : null
        }
      >
        <Input
          type="text"
          inputMode="numeric"
          value={value.targetSize}
          onChange={(e) => onChange({ ...value, targetSize: e.target.value })}
          disabled={disabled}
        />
      </Field>
      <Field
        label={tc('leaderFacts.topicsLabel')}
        description={tc('leaderFacts.topicsDescription', { max: CIRCLE_MAX_TOPICS })}
        error={
          selectedTopicIds.length > CIRCLE_MAX_TOPICS
            ? tc('leaderFacts.errorTopicsOverCap', { count: selectedTopicIds.length, max: CIRCLE_MAX_TOPICS })
            : null
        }
      >
        <PillFilter
          multi
          value={selectedTopicIds}
          onChange={(next) => onChange({ ...value, topicIds: next })}
          options={topics.options}
          disabled={disabled}
        />
      </Field>
    </>
  );
}
